/* eslint-disable no-underscore-dangle */

import { create, SheetsRegistry } from 'jss';
import preset from 'jss-preset-default';
import { createMuiTheme } from '@material-ui/core/styles';
import createGenerateClassName from '@material-ui/core/styles/createGenerateClassName';
import palette from './palette';
import transitions from './transitions';
import shadows from './shadows';
import pxToRem from './pxToRem';
import pxToEm from './pxToEm';
import hexToRgb from './hexToRgb';

const theme = createMuiTheme({
  palette,
  transitions,
  shadows,
  typography: {
    title: {
      fontSize: 20,
      letterSpacing: '0.03em',
      lineHeight: 1.2,
      color: palette.secondary[500],
    },
    subheading: {
      letterSpacing: pxToEm(0.5),
    },
    caption: {
      fontSize: pxToRem(12),
      letterSpacing: pxToEm(0.5, 12),
      color: palette.grey[700],
    },
    body1: {
      color: palette.secondary[800],
      letterSpacing: pxToEm(0.5),
    },
    button: {
      fontWeight: 800,
      letterSpacing: '0.05em',
    },
  },
  mixins: {
    toolbar: {
      minHeight: 0,
    },
  },
  pointsbreak: {
    keys: [
      'xs',
      'sm',
      'md',
      'lg',
      'xl',
    ],
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1440,
      xl: 1980,
    },
  },
});
theme.overrides = {
  MuiButtonBase: {
    root: {
      verticalAlign: 'initial',
    },
  },
  MuiButton: {
    root: {
      lineHeight: '1.3em',
      borderRadius: 5,
      minWidth: 70,
    },
    raised: {
      color: palette.text,
      backgroundColor: palette.common.white,
      fontWeight: '400',
      '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderRadius: 5,
        border: `1px solid ${palette.primary.A100}`,
        transition: `opacity ${transitions.duration.short} ${transitions.easing.easeInOut}`,
      },
      '&:hover': {
        '&:after': {
          opacity: '0',
        },
      },
      '&$disabled': {
        color: palette.text.primary,
        backgroundColor: palette.common.white,
        boxShadow: shadows[2],
      },
    },
    contained: {
      boxShadow: 'none',
    },
    raisedPrimary: {
      color: 'white',
      border: 'none',
      fontWeight: '700',
      backgroundColor: palette.primary[500],
      '&:after': {
        display: 'none',
      },
      '&$disabled': {
        color: 'white',
        backgroundColor: palette.primary[500],
        boxShadow: shadows[2],
      },
    },
    disabled: {
      opacity: '0.5',
    },
    fab: {
      width: 50,
      height: 50,
      boxShadow: shadows[2],
      '&:after': {
        display: 'none',
      },
    },
    mini: {
      width: 24,
      height: 24,
      minHeight: 24,
      fontSize: pxToRem(12),
      lineHeight: pxToEm(14, 12),
      textTransform: 'none',
    },
  },
  MuiIconButton: {
    root: {
      color: palette.grey[600],
    },
    disabled: {
      opacity: '0.5',
    },
  },
  MuiTypography: {
    colorSecondary: {
      color: palette.grey[700],
    },
    title: {
      color: palette.secondary[650],
    },
    display4: {
      fontSize: pxToRem(200),
      lineHeight: pxToEm(200, 200),
      letterSpacing: pxToEm(6, 200),
      color: palette.primary.A400,
    },
  },
  MuiPaper: {
    rounded: {
      borderRadius: 10,
    },
  },
  MuiTable: {
    root: {
      overflow: 'visible',
    },
  },
  MuiTableRow: {
    root: {
      '&$hover': {
        '&:hover, &:focus': {
          backgroundColor: palette.primary.A100,
          cursor: 'pointer',
        },
      },
    },
    hover: {
      '&:hover, &:focus': {
        backgroundColor: palette.primary.A100,
        cursor: 'pointer',
      },
    },
  },
  MuiTableCell: {
    root: {
      color: palette.secondary[800],
      borderBottom: `1px solid ${palette.primary.A400}`,
      padding: '13px 8px 13px 8px',
      [theme.breakpoints.down('lg')]: {
        paddingLeft: 4,
        paddingRight: 4,
      },
      [theme.breakpoints.down('sm')]: {
        padding: '8px 4px',
      },
      '&:first-child': {
        paddingLeft: 24,
        [theme.breakpoints.down('sm')]: {
          paddingLeft: 4,
        },
      },
      '&:last-child': {
        [theme.breakpoints.down('sm')]: {
          paddingRight: 4,
        },
      },
    },
    paddingNone: {
      padding: 0,
      '&:first-child': {
        paddingLeft: 0,
      },
    },
    paddingCheckbox: {
      padding: '0 6px 0 10px',
    },
    head: {
      fontSize: 12,
      letterSpacing: '0.03em',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      color: palette.secondary[450],
      backgroundColor: palette.primary.A200,
      borderBottom: 'none',
    },
  },
  MuiTablePagination: {
    select: {
      paddingLeft: 0,
      paddingRight: 22,
      textAlign: 'right',
      width: 34,
    },
    selectRoot: {
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        marginRight: 0,
      },
    },
    actions: {
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
      },
    },
  },
  MuiMenu: {
    paper: {
      borderRadius: 5,
    },
  },
  MuiModal: {
    root: {
      zIndex: 2500,
    },
  },
  MuiTabs: {
    root: {
      minHeight: 38,
    },
    indicator: {
      height: 3,
    },
  },
  MuiTab: {
    root: {
      height: 38,
      minHeight: 38,
      minWidth: 90,
      color: palette.secondary[650],
      opacity: 1,
    },
    labelContainer: {
      paddingLeft: 15,
      paddingRight: 15,
    },
    label: {
      fontSize: pxToRem(12),
      letterSpacing: pxToEm(0.25, 12),
    },
  },
  MuiFormGroup: {
    root: {
      flexDirection: 'row',
    },
  },
  MuiFormLabel: {
    root: {
      fontSize: pxToRem(12),
      letterSpacing: pxToEm(0.5),
      fontWeight: '700',
      color: palette.grey[600],
      marginBottom: 10,
      [theme.breakpoints.down('sm')]: {
        marginBottom: 4,
      },
      paddingTop: 4,
    },
    focused: {
      color: palette.grey[600],
    },
  },
  MuiFormControlLabel: {
    root: {
      marginRight: 0,
    },
    disabled: {
      color: `rgba(${hexToRgb(palette.secondary[800])}, 0.4)`,
    },
  },
  MuiCheckbox: {
    root: {
      color: palette.primary.A400,
    },
  },
  MuiInput: {
    root: {
      alignItems: 'center',
    },
    input: {
      width: '100%',
    },
  },
  MuiTextarea: {
    root: {
      height: 19,
    },
  },
  MuiInputAdornment: {
    root: {
      display: 'flex',
      color: palette.primary.A400,
    },
    positionStart: {
      marginRight: -5,
      marginLeft: 10,
    },
    positionEnd: {
      marginRight: 10,
      marginLeft: -5,
    },
  },
  MuiRadio: {
    root: {
      color: palette.primary.A400,
    },
    disabled: {
      color: `rgba(${hexToRgb(palette.secondary[800])}, 0.4)`,
    },
  },
  MuiSwitch: {
    root: {
      marginTop: -8,
      marginBottom: -8,
    },
    icon: {
      boxShadow: 'none',
      marginRight: -3,
      color: palette.common.white,
    },
    bar: {
      height: 24,
      marginTop: -12,
      borderRadius: 20,
      opacity: 1,
      backgroundColor: palette.primary.A400,
    },
    checked: {
      transform: 'translateX(10px)',
      '& + $bar': {
        opacity: 1,
      },
    },
    colorSecondary: {
      '&$checked': {
        color: palette.primary[500],
        '& + $bar': {
          backgroundColor: palette.primary[500],
        },
      },
    },
  },
  MuiMenuItem: {
    root: {
      display: 'block',
      maxWidth: 260,
      paddingRight: 20,
      paddingLeft: 20,
      fontSize: pxToRem(14),
      '&$selected': {
        backgroundColor: palette.grey[200],
      },
    },
  },
  MuiDialog: {
    paper: {
      maxHeight: 'none',
      overflowY: 'visible',
    },
    paperWidthXs: {
      maxWidth: 360,
      minWidth: 360,
    },
    paperWidthSm: {
      maxWidth: 555,
      minWidth: 555,
    },
    paperWidthMd: {
      maxWidth: 750,
      minWidth: 750,
    },
    paperFullScreen: {
      background: 'transparent',
    },
  },
  MuiDialogTitle: {
    root: {
      padding: 0,
    },
  },
  MuiDialogContent: {
    root: {
      padding: '0 24px',
      overflowY: 'visible',
      '& + div': {
        borderTop: `1px solid ${palette.primary.A100}`,
      },
    },
  },
  MuiDialogActions: {
    root: {
      position: 'relative',
      margin: 0,
      padding: '8px 14px',
      [theme.breakpoints.down('sm')]: {
        padding: '8px 8px',
      },
    },
    action: {
      minWidth: 80,
      margin: '0 10px',
    },
  },
  MuiTooltip: {
    tooltip: {
      minHeight: 0,
      whiteSpace: 'pre-line',
      opacity: 1,
      fontSize: pxToRem(14, 16),
      lineHeight: pxToEm(16, 14),
      '&$open': {
        opacity: 1,
      },
    },
    tooltipPlacementRight: {
      margin: '0 15px',
    },
  },
  MuiAvatar: {
    root: {
      width: 44,
      height: 44,
      backgroundColor: 'transparent',
    },
    colorDefault: {
      backgroundColor: 'transparent',
    },
  },
  MuiChip: {
    root: {
      display: 'inline-flex',
      verticalAlign: 'middle',
      backgroundColor: palette.common.white,
      color: palette.primary[500],
    },
    deletable: {
      '&:focus': {
        backgroundColor: palette.grey[200],
      },
    },
  },
  MuiExpansionPanel: {
    root: {
      '&:before': {
        display: 'none',
      },
    },
    expanded: {
      margin: 0,
    },
  },
  MuiExpansionPanelSummary: {
    root: {
      padding: '0 48px 0 0',
      '&$expanded': {
        minHeight: 0,
      },
    },
    expanded: {
      minHeight: 0,
    },
    content: {
      '& > :last-child': {
        paddingRight: 0,
      },
      '&$expanded': {
        margin: 0,
      },
    },
    expandIcon: {
      right: 0,
    },
  },
  MuiExpansionPanelDetails: {
    root: {
      flexGrow: 1,
    },
  },
  MuiSnackbar: {
    root: {
      left: '50%',
      right: 'auto',
      transform: 'translateX(-50%)',
      zIndex: 2600,
    },
    anchorOriginBottomCenter: {
      bottom: 60,
    },
  },
  MuiSnackbarContent: {
    root: {
      color: palette.grey[700],
      backgroundColor: palette.common.white,
      borderRadius: 4,
      height: 64,
      padding: '8px 24px',
    },
    action: {
      marginRight: 0,
    },
  },
  MuiCircularProgress: {
    indeterminate: {
      animation: 'none',
    },
    colorSecondary: {
      color: palette.common.white,
    },
  },
  MuiGrid: {
    item: {
      flex: '0 0 auto',
    },
    'spacing-xs-16': {
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% + 8px)',
        margin: -4,
        
      },
      '& > $item': {
        [theme.breakpoints.down('sm')]: {
          padding: 4,
        },
      },
    },
    'spacing-xs-24': {
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% + 8px)',
        margin: -4,
        
      },
      '& > $item': {
        [theme.breakpoints.down('sm')]: {
          padding: 4,
        },
      },
    },
  },
  MuiCollapse: {
    container: {
      transition: `height ${transitions.duration.short}ms ${transitions.easing.easeInOut}`,
    },
  },
};


// Configure JSS
const jss = create(preset());

export const sheetsManager = new Map();

export default function createContext() {
  return {
    jss,
    theme,
    // This is needed in order to deduplicate the injection of CSS in the page.
    sheetsManager,
    // This is needed in order to inject the critical CSS.
    sheetsRegistry: new SheetsRegistry(),
    generateClassName: createGenerateClassName(),
  };
}



// WEBPACK FOOTER //
// ./src/styles/createContext.js